import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  X,
  Loader2,
  FileDown,
  Link2,
  FileText,
  Check,
  RotateCcw,
  Upload,
} from 'lucide-react'
import {
  parseOpenApiSpec,
  confirmOpenApiImport,
  fetchCustomTools,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import {
  customToolFormInputClass,
  customToolFormLabelClass,
  customToolFormSelectClass,
  customToolHttpMethodColors,
  useCustomToolAuthFields,
} from './customToolShared'

export interface ImportSpecModalProps {
  onClose: () => void
  /** When true, omit top title bar (use parent Modal.Header). */
  hideChrome?: boolean
}

const ImportSpecModal: React.FC<ImportSpecModalProps> = ({ onClose, hideChrome = false }) => {
  const dispatch = useDispatch()
  const [tab, setTab] = useState<'url' | 'file'>('url')
  const [specUrl, setSpecUrl] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [fileText, setFileText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [endpoints, setEndpoints] = useState<any[]>([])
  const [parseMeta, setParseMeta] = useState<any>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [names, setNames] = useState<Record<string, string>>({})
  const [globalAuthType, setGlobalAuthType] = useState('none')
  const [globalAuth, setGlobalAuth] = useState<Record<string, string>>({})
  const { authField } = useCustomToolAuthFields(globalAuth, setGlobalAuth)

  const endpointIds = useMemo(() => endpoints.map((_, i) => String(i)), [endpoints])
  const selectedCount = useMemo(() => endpointIds.filter((id) => selected[id]).length, [endpointIds, selected])

  const reset = () => {
    setEndpoints([]); setParseMeta(null); setSelected({}); setNames({})
    setSpecUrl(''); setBaseUrl(''); setFileText('')
  }

  const handleClose = () => { reset(); onClose() }

  const handleParse = async () => {
    setParsing(true)
    try {
      const body: any = {}
      if (tab === 'url') body.specUrl = specUrl.trim()
      else body.specText = fileText
      if (baseUrl.trim()) body.baseUrl = baseUrl.trim()
      const data = await dispatch(parseOpenApiSpec(body) as any).unwrap()
      const eps = data.endpoints || []
      setParseMeta(data); setEndpoints(eps)
      const sel: Record<string, boolean> = {}
      const nm: Record<string, string> = {}
      eps.forEach((e: any, i: number) => { sel[String(i)] = eps.length <= 50; nm[String(i)] = e.suggested_name || `op_${i}` })
      setSelected(sel); setNames(nm)
      if (data.warning) toast.warn(data.warning)
      if (eps.length === 0) toast.info('No operations found in spec.')
    } catch (e: any) {
      toast.error(e?.error || e?.message || 'Parse failed')
    } finally { setParsing(false) }
  }

  const toggleAll = (on: boolean) => {
    const next: Record<string, boolean> = {}
    endpointIds.forEach((id) => { next[id] = on })
    setSelected(next)
  }

  const buildToolsPayload = () =>
    endpoints
      .map((e, i) => {
        const id = String(i)
        if (!selected[id]) return null
        return {
          name: (names[id] || e.suggested_name || '').trim(),
          suggested_name: e.suggested_name, description: e.description,
          endpoint_url: e.endpoint_url, http_method: e.method,
          parameters_schema: e.parameters_schema,
          auth_type: globalAuthType, auth_config: { ...globalAuth }, headers: null,
        }
      })
      .filter(Boolean)

  const handleImport = async () => {
    const tools = buildToolsPayload()
    if (!tools.length) { toast.error('Select at least one endpoint'); return }
    setImporting(true)
    try {
      const res = await dispatch(confirmOpenApiImport({ tools }) as any).unwrap()
      toast.success(`Imported ${res.created_count} tool(s)${res.skipped ? `, ${res.skipped} skipped` : ''}`)
      await dispatch(fetchCustomTools() as any)
      handleClose()
    } catch (e: any) {
      toast.error(e?.error || 'Import failed')
    } finally { setImporting(false) }
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-bg-white">
      {!hideChrome && (
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FileDown className="tw-w-4 tw-h-4 tw-text-blue-600" />
            <h2 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">Import from JSON Schema</h2>
          </div>
          <button type="button" onClick={handleClose} className="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer">
            <X className="tw-w-4 tw-h-4" />
          </button>
        </div>
      )}

      {/* Body */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-5 tw-py-5">
        {endpoints.length === 0 ? (
          <div className="tw-space-y-5">
            <div className="tw-flex tw-rounded-lg tw-bg-gray-100 tw-p-0.5">
              <button onClick={() => setTab('url')} className={`tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-transition-all tw-border-0 tw-cursor-pointer ${tab === 'url' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'}`}>
                <Link2 className="tw-w-3.5 tw-h-3.5" /> From URL
              </button>
              <button onClick={() => setTab('file')} className={`tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-transition-all tw-border-0 tw-cursor-pointer ${tab === 'file' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'}`}>
                <FileText className="tw-w-3.5 tw-h-3.5" /> Paste / File
              </button>
            </div>

            {tab === 'url' ? (
              <div>
                <label className={customToolFormLabelClass}>Spec URL (https)</label>
                <input className={customToolFormInputClass} value={specUrl} onChange={(e) => setSpecUrl(e.target.value)} placeholder="https://api.example.com/openapi.json" />
              </div>
            ) : (
              <div className="tw-space-y-3">
                <div>
                  <label className={customToolFormLabelClass}>JSON or YAML</label>
                  <textarea className={`${customToolFormInputClass} tw-font-mono tw-text-xs tw-resize-y`} rows={8} value={fileText} onChange={(e) => setFileText(e.target.value)} placeholder="Paste your OpenAPI spec here..." />
                </div>
                <label className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 hover:tw-border-gray-400 tw-cursor-pointer tw-text-sm tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors">
                  <Upload className="tw-w-4 tw-h-4" /> Choose file
                  <input type="file" accept=".json,.yaml,.yml" className="tw-hidden" onChange={(ev) => { const f = ev.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setFileText(String(r.result || '')); r.readAsText(f) }} />
                </label>
              </div>
            )}

            <div>
              <label className={customToolFormLabelClass}>Base URL override (optional)</label>
              <input className={customToolFormInputClass} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com" />
              <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1">Only needed if the spec has no servers block</p>
            </div>

            <button onClick={handleParse} disabled={parsing || (tab === 'url' ? !specUrl.trim() : !fileText.trim())} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors">
              {parsing ? <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" /> : <FileDown className="tw-w-3.5 tw-h-3.5" />}
              {parsing ? 'Parsing...' : 'Parse spec'}
            </button>
          </div>
        ) : (
          <div className="tw-space-y-5">
            <div className="tw-flex tw-items-center tw-justify-between">
              <p className="tw-text-sm tw-text-gray-600 tw-m-0">
                <span className="tw-font-semibold tw-text-gray-900">{endpoints.length}</span> endpoint{endpoints.length !== 1 ? 's' : ''} found
                {selectedCount > 0 && <span className="tw-ml-2 tw-text-blue-600">{selectedCount} selected</span>}
              </p>
              <div className="tw-flex tw-items-center tw-gap-1">
                <button onClick={() => toggleAll(true)} className="tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-md hover:tw-bg-blue-50 tw-transition-colors">All</button>
                <button onClick={() => toggleAll(false)} className="tw-text-xs tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors">None</button>
                <button onClick={reset} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors">
                  <RotateCcw className="tw-w-3 tw-h-3" /> Reset
                </button>
              </div>
            </div>

            <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
              <div className="tw-max-h-[280px] tw-overflow-y-auto tw-divide-y tw-divide-gray-100">
                {endpoints.map((e, i) => {
                  const id = String(i)
                  const methodClass =
                    customToolHttpMethodColors[e.method?.toUpperCase()] || customToolHttpMethodColors.GET
                  return (
                    <label key={id} className={`tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5 tw-cursor-pointer tw-transition-colors ${selected[id] ? 'tw-bg-blue-50/40' : 'hover:tw-bg-gray-50'}`}>
                      <input type="checkbox" checked={!!selected[id]} onChange={() => setSelected((s) => ({ ...s, [id]: !s[id] }))} className="tw-mt-0.5 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-blue-500 tw-cursor-pointer" />
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <span className={`tw-inline-flex tw-items-center tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-ring-1 tw-ring-inset ${methodClass}`}>{e.method?.toUpperCase()}</span>
                          <span className="tw-text-xs tw-font-mono tw-text-gray-600 tw-truncate">{e.path}</span>
                        </div>
                        {e.description && <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-truncate">{e.description}</p>}
                        <input
                          className="tw-mt-1 tw-w-full tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-text-gray-700 tw-outline-none focus:tw-border-blue-400 focus:tw-ring-1 focus:tw-ring-blue-400/30"
                          value={names[id] ?? ''} onChange={(ev) => { ev.stopPropagation(); setNames((n) => ({ ...n, [id]: ev.target.value })) }}
                          onClick={(ev) => ev.stopPropagation()} placeholder="Tool name"
                        />
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            <div>
              <label className={customToolFormLabelClass}>Auth for imported tools</label>
              <select className={customToolFormSelectClass} value={globalAuthType} onChange={(e) => setGlobalAuthType(e.target.value)}>
                <option value="none">None</option>
                <option value="api_key">API Key</option>
                <option value="bearer">Bearer Token</option>
                <option value="basic">Basic Auth</option>
                <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
              </select>
              {globalAuthType === 'api_key' && (
                <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">
                  {authField('Key name', 'key_name')}{authField('Key value', 'key_value', 'password')}
                  <select className={customToolFormSelectClass} value={globalAuth.in || 'header'} onChange={(e) => setGlobalAuth((a) => ({ ...a, in: e.target.value }))}><option value="header">Send in Header</option><option value="query">Send in Query</option></select>
                </div>
              )}
              {globalAuthType === 'bearer' && <div className="tw-mt-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">{authField('Token', 'token', 'password')}</div>}
              {globalAuthType === 'basic' && <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">{authField('Username', 'username')}{authField('Password', 'password', 'password')}</div>}
              {globalAuthType === 'oauth2_client_credentials' && (
                <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">
                  {authField('Token URL', 'token_url')}{authField('Client ID', 'client_id')}{authField('Client Secret', 'client_secret', 'password')}{authField('Scope', 'scope')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-4 tw-border-t tw-border-gray-100 tw-shrink-0 tw-bg-gray-50/50">
        {endpoints.length > 0 && (
          <button onClick={handleImport} disabled={importing || selectedCount === 0} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors">
            {importing ? <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" /> : <Check className="tw-w-3.5 tw-h-3.5" />}
            {importing ? 'Importing...' : `Import ${selectedCount} tool${selectedCount !== 1 ? 's' : ''}`}
          </button>
        )}
        <button onClick={handleClose} className="tw-ml-auto tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
          Close
        </button>
      </div>
    </div>
  )
}

export default ImportSpecModal
